import type { PetDef } from '@vm/shared';
import type { CosmeticDef } from './cosmetics';

/**
 * Companion pets: an earned loadout slot. Every role is a sidegrade: tuned so a pet helps but never
 * decides a fight on its own (validated with tools/bot-bench.ts --match). Unlocked by level and
 * bought with coins; looks are sold separately as pet styles.
 * List order is the wire id: append, never reorder.
 */
const LIST: Omit<PetDef, 'num'>[] = [
  {
    id: 'owlet', name: 'Owlet', role: 'scout',
    description: 'Every few seconds it hoots and marks nearby enemies for you, even behind walls.',
    unlockLevel: 1, price: 0, currency: 'coins',
    hp: 50, reform: 20, interval: 8, range: 24, duration: 3,
    colors: [0x8a6a4a, 0xe8d8b0, 0xffb030],
  },
  {
    id: 'sprout', name: 'Sprout', role: 'medic',
    description: 'A little leafy healer. Patches you up once you have been out of the fight for a moment.',
    unlockLevel: 3, price: 900, currency: 'coins',
    hp: 60, reform: 20, amount: 5, duration: 3,
    colors: [0x5ab84a, 0xff9ac8, 0xffe060],
  },
  {
    id: 'drillbit', name: 'Drillbit', role: 'digger',
    description: 'Shoot a wall or the ground and Drillbit zooms over to chew a hole through it.',
    unlockLevel: 5, price: 1500, currency: 'coins',
    hp: 60, reform: 20, interval: 4, range: 14, radius: 0.7, amount: 45,
    colors: [0xf0c030, 0x2a2a30, 0xb0b8c0],
  },
  {
    id: 'bulwark', name: 'Bulwark', role: 'guard',
    description: 'A floating shell that soaks up part of the damage aimed at you until it cracks.',
    unlockLevel: 8, price: 2500, currency: 'coins',
    hp: 120, reform: 25, amount: 0.3,
    colors: [0x3a8a7a, 0x8a9098, 0x5ab0ff],
  },
  {
    id: 'chomper', name: 'Chomper', role: 'attacker',
    description: 'Dives at enemies near you and nips them. Enemies can shoot it down.',
    unlockLevel: 11, price: 3200, currency: 'coins',
    hp: 70, reform: 20, damage: 10, interval: 1.1, range: 12,
    colors: [0x7a4ab0, 0xf0f0f0, 0xff4a4a],
  },
];

export const PETS: PetDef[] = LIST.map((p, i) => ({ ...p, num: i + 1 }));
export const PET_BY_ID: Record<string, PetDef> = Object.fromEntries(PETS.map((p) => [p.id, p]));
export const PET_BY_NUM: PetDef[] = [];
for (const p of PETS) PET_BY_NUM[p.num] = p;

/** Kill events credit pets with weapon num PET_WEAPON_BASE + pet num (the kill feed shows the pet). */
export const PET_WEAPON_BASE = 1000;

export function petByWeaponNum(weapon: number): PetDef | undefined {
  return weapon > PET_WEAPON_BASE ? PET_BY_NUM[weapon - PET_WEAPON_BASE] : undefined;
}

/** Pet styles: cosmetic recolors for any pet (the only way pets are monetized). */
export interface PetStyleDef extends CosmeticDef {
  /** Replaces the pet's [primary, secondary, accent] colors. */
  colors: [number, number, number];
  /** Emissive accent glow color, if any. */
  glow?: number;
}

export const PET_STYLES: PetStyleDef[] = [
  { id: 'camo', name: 'Field Camo', rarity: 'common', price: 1200, currency: 'coins', unlockLevel: 4, source: 'shop', colors: [0x5a6a3a, 0x3a4228, 0x8a7a4a] },
  { id: 'candy', name: 'Candy Pop', rarity: 'common', price: 1500, currency: 'coins', unlockLevel: 6, source: 'shop', colors: [0xff8ac8, 0x8adcff, 0xfff08a] },
  { id: 'frost', name: 'Frostbite', rarity: 'epic', price: 180, currency: 'gems', unlockLevel: 1, source: 'shop', colors: [0xbfe8ff, 0xffffff, 0x4ab8ff], glow: 0x6ad0ff },
  { id: 'ember', name: 'Ember', rarity: 'epic', price: 180, currency: 'gems', unlockLevel: 1, source: 'shop', colors: [0xd8402a, 0x2a1a18, 0xffb030], glow: 0xff7a2a },
  { id: 'shadow', name: 'Shadow', rarity: 'epic', price: 250, currency: 'gems', unlockLevel: 1, source: 'shop', colors: [0x2a2a38, 0x14141c, 0xb46aff], glow: 0xb46aff },
  { id: 'neon', name: 'Neon Rush', rarity: 'epic', price: 0, currency: 'gems', unlockLevel: 1, source: 'battlepass', colors: [0x1a1a2a, 0x2a2a3a, 0x4af0d0], glow: 0x4af0d0 },
  { id: 'gold', name: 'Solid Gold', rarity: 'legendary', price: 300, currency: 'gems', unlockLevel: 1, source: 'shop', colors: [0xe8b830, 0xfff0b0, 0xffffff], glow: 0xffd24a },
];
export const PET_STYLE_BY_ID: Record<string, PetStyleDef> = Object.fromEntries(PET_STYLES.map((s) => [s.id, s]));

/** Colors of a pet in a style (unknown/empty style = the pet's own colors). */
export function petColors(pet: PetDef, styleId?: string): { colors: [number, number, number]; glow?: number } {
  const s = styleId ? PET_STYLE_BY_ID[styleId] : undefined;
  return s ? { colors: s.colors, glow: s.glow } : { colors: pet.colors };
}
